import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";
import { AUTH } from "@/constants/testIds";
import { extractAxiosError } from "@/lib/api-errors";

const AgentLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.id) return <Navigate to="/agent/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/agent/dashboard");
    } catch (err) {
      setError(extractAxiosError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={AUTH.loginPage} className="min-h-screen bg-cf-black text-cf-text relative cf-radial">
      <div className="cf-grain absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-10 flex items-center justify-between">
        <CFLogo variant="paper" />
        <Link to="/" className="text-xs uppercase tracking-[0.22em] text-cf-muted hover:text-cf-gold cf-link-underline">
          ← Back to site
        </Link>
      </div>

      <div className="relative z-10 max-w-md mx-auto px-6 mt-20">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-cf-gold" />
          <span className="cf-overline">Agent Portal</span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
          Sign in to the <span className="italic text-cf-gold">Capless</span> agent console.
        </h1>
        <p className="text-cf-muted text-sm leading-relaxed mt-5">
          Restricted to authorized Capless Funding credit officers and account agents.
        </p>

        <form onSubmit={submit} className="mt-12 bg-cf-surface/60 border border-white/5 p-8 md:p-10">
          <div className="space-y-2">
            <label className="cf-overline">Email</label>
            <input
              data-testid={AUTH.loginEmail}
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="cf-input" placeholder="agent@caplessfunding.com" autoComplete="email"
            />
          </div>
          <div className="space-y-2 mt-6">
            <label className="cf-overline">Password</label>
            <input
              data-testid={AUTH.loginPassword}
              required type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="cf-input" placeholder="••••••••" autoComplete="current-password"
            />
          </div>
          {error && (
            <p data-testid={AUTH.loginError} className="mt-6 text-sm text-red-400">{error}</p>
          )}
          <button
            data-testid={AUTH.loginSubmit}
            type="submit" disabled={loading}
            className="cf-btn-primary w-full justify-center mt-10 disabled:opacity-50"
          >
            {loading ? "Signing in..." : <>Sign in <ArrowUpRight size={16} strokeWidth={1.5} /></>}
          </button>
          <div className="flex items-center gap-2 text-cf-muted text-xs mt-6">
            <Lock size={12} strokeWidth={1.4} /> Encrypted session · no credit pulls
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentLogin;
